import { Icon, IconSprite } from '@ellucian/ds-icons/lib';
import {
    List,
    ListItem,
    ListItemText,
    Popover,
    Typography,
} from '@ellucian/react-design-system/core';
import { withStyles } from '@ellucian/react-design-system/core/styles';
import {
    borderRadiusSmall,
    borderWidthThicker,
    borderWidthThin,
    colorBorderGlobalFocus,
    colorBrandCtaHover,
    colorGlobalBorderDefault,
    colorTextPrimary
} from '@ellucian/react-design-system/core/styles/tokens';
import classnames from 'classnames';
import React = require('react');
import { Condition } from './models/Group';

const styles = (theme) => ({
    container: {
        'alignSelf': 'flex-start',
        'background': theme.palette.common.white,
        'border': `${borderWidthThin} solid ${colorGlobalBorderDefault}`,
        'borderRadius': borderRadiusSmall,
        'color': colorTextPrimary,
        'marginLeft': '0px',
        'marginTop': '-33px',
        'position': 'relative',
        'text-align': 'center',
        'width': '38px',
        'zIndex': 50,
    },
    containerCentered: {
        marginTop: ['-8px', '!important'],
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
    },
    containerEditable: {
        '&:hover': {
            backgroundColor: colorBorderGlobalFocus,
            color: theme.palette.common.white
        },
        'border': `${borderWidthThin} solid ${colorBorderGlobalFocus}`,
        'color': colorBorderGlobalFocus,
        'cursor': 'pointer',
    },
    containerSelected: {
        backgroundColor: colorBorderGlobalFocus,
        color: theme.palette.common.white
    },
    groupButtonIcon: {
        display: 'block',
        marginBottom: '2px',
        marginLeft: '10px',
        marginTop: '-2px',
        paddingLeft: '0px',
        paddingTop: '0px',
    },
    groupButtonText: {
        color: 'inherit'
    },
    list: {
        border: `${borderWidthThin} solid ${colorGlobalBorderDefault}`,
        borderRadius: borderWidthThicker,
        zIndex: theme.zIndex.actionMenu,
    },
    listItem: {
        '&:hover': {
            backgroundColor: colorBrandCtaHover,
            color: theme.palette.common.white
        },
        'padding': '1.25rem',
    },
    listItemDisabled: {
        cursor: 'default'
    },
    listItemText: {
        color: 'inherit'
    }
});

export interface IGroupButtonResources {
    addNewRule: string;
    addSelectedRules: string;
    and: string;
    addNewGroup: string;
    changeTo: string;
    or: string;
    ungroup: string;
}

export interface IGroupButtonProps {
    center: boolean;
    classes: any;
    condition: Condition;
    depth: number;
    disableAddGroupFromSelectedRules: boolean;
    disableAddSelectedRules: boolean;
    disableUngroup: boolean;
    lineId?: string;
    groupId: string;
    isSelected: boolean;
    ruleIndex: string;
    strings: IGroupButtonResources;
    onAddGroupFromSelectedRules: (groupId: string) => void;
    onAddRule: (groupId: string) => void;
    onAddSelectedRules: (groupId: string) => void;
    onChangeGroupCondition: (groupId: string, condition: Condition) => void;
    onSelectGroup: (groupId: string) => void;
    onUngroup: (groupId: string) => void;
    onUnselectGroup: (groupId: string) => void;
    readOnly?: boolean;
}

interface IGroupButtonState {
    popoverAnchorElement: any;
}

const idPrefix = 'queryBuilderGroupButton';

export class GroupButtonInternal extends React.Component<IGroupButtonProps, IGroupButtonState> {
    public static defaultProps = {
        readOnly: false,
        strings: {
            addNewGroup: 'Create New Group from Selected Rules',
            addNewRule: 'Add New Rule',
            addSelectedRules: 'Move Selected Rules to Group',
            and: Condition.And,
            changeTo: 'Change to',
            or: Condition.Or,
            ungroup: 'Ungroup',
        },
    };

    constructor(props: IGroupButtonProps) {
        super(props);

        this.state = {
            popoverAnchorElement: null,
        };
    }

    public render() {
        const {
            center,
            classes,
            condition,
            depth,
            disableAddGroupFromSelectedRules,
            disableAddSelectedRules,
            disableUngroup,
            groupId,
            lineId,
            isSelected,
            onAddGroupFromSelectedRules,
            onAddRule,
            onAddSelectedRules,
            onChangeGroupCondition,
            onSelectGroup,
            onUngroup,
            onUnselectGroup,
            readOnly,
            ruleIndex,
            strings,
        } = this.props;

        const {
            popoverAnchorElement
        } = this.state;

        const open = !!popoverAnchorElement;

        const idSuffix = `${depth}_${ruleIndex}`;

        const listItem = (id: string, text: string, disabled: boolean = false, onClick: () => void = null) => {
            const classNames = classnames(classes.listItem, {
                [classes.listItemDisabled]: disabled,
            });

            return (
                <ListItem
                    disabled={disabled}
                    classes={{ root: classNames }}
                    id={`${idPrefix}_listItem_${id}_${idSuffix}`}
                    onClick={() => {
                        if (!disabled) {
                            if (onClick) {
                                onClick();
                            }
                            if (open && onUnselectGroup) {
                                onUnselectGroup(groupId);
                            }
                        }
                        this.setState({popoverAnchorElement: null});
                    }}
                >
                    <ListItemText disableTypography={true}>
                        <Typography variant="body2" className={classes.listItemText}>
                            {text}
                        </Typography>
                    </ListItemText>
                </ListItem>
            );
        };

        return (
            <>
                <Popover
                    id={`${idPrefix}_menuPopup_${idSuffix}`}
                    open={open}
                    anchorEl={popoverAnchorElement}
                    onClose={() => {
                        if (open && onUnselectGroup) {
                            onUnselectGroup(groupId);
                        }
                        this.setState({popoverAnchorElement: null});
                    }}
                    anchorOrigin={{
                        horizontal: 'left',
                        vertical: 'bottom',
                    }}
                    transformOrigin={{
                        horizontal: 'center',
                        vertical: 'top',
                    }}
                >
                    <List className={classes.list} id={`${idPrefix}_menuList_${idSuffix}`}>
                        {listItem('changeTo',
                            `${strings.changeTo} ${condition === Condition.And ? strings.or : strings.and}`,
                            false, () => {
                            if (onChangeGroupCondition) {
                                // tslint:disable-next-line: max-line-length
                                onChangeGroupCondition(groupId, condition === Condition.And ? Condition.Or : Condition.And);
                            }
                        })}
                        {listItem('addNewRule', strings.addNewRule, false, () => {
                            if (onAddRule) {
                                onAddRule(groupId);
                            }
                        })}
                        {listItem('addNewGroup', strings.addNewGroup, disableAddGroupFromSelectedRules, () => {
                            if (onAddGroupFromSelectedRules) {
                                onAddGroupFromSelectedRules(groupId);
                            }
                        })}
                        {listItem('addSelectedRules', strings.addSelectedRules, disableAddSelectedRules, () => {
                            if (onAddSelectedRules) {
                                onAddSelectedRules(groupId);
                            }
                        })}
                        {listItem('ungroup', strings.ungroup, disableUngroup, () => {
                            if (onUngroup) {
                                onUngroup(groupId);
                            }
                        })}
                    </List>
                </Popover>
                <div
                    id={`${idPrefix}_groupButton_${idSuffix}`}
                    data-line-id={lineId}
                    className={classnames(classes.container, {
                        [classes.containerSelected]: isSelected,
                        [classes.containerCentered]: center,
                        [classes.containerEditable]: !readOnly
                    })}
                    onClick={(e) => {
                        if (readOnly) {
                            return;
                        }
                        if (onUnselectGroup) {
                            onUnselectGroup(groupId);
                        }
                        this.setState({...this.state, popoverAnchorElement: e.currentTarget});
                    }}
                    onMouseEnter={() => onSelectGroup && onSelectGroup(groupId)}
                    onMouseLeave={() => {
                        if (!open && onUnselectGroup) {
                            onUnselectGroup(groupId);
                        }
                    }}
                >
                    <IconSprite />
                    <Typography
                        variant="body2"
                        className={classes.groupButtonText}
                        id={`${idPrefix}_groupButtonText_${idSuffix}`}
                    >
                        {condition === Condition.And ? strings.and : strings.or}
                        <Icon
                            className={classes.groupButtonIcon}
                            id={`${idPrefix}_groupButtonIcon_${idSuffix}`}
                            name="chevron-down"
                        />
                    </Typography>
                </div>
            </>
        );
    }
}

export const GroupButton = [
    withStyles(styles),
].reduce((it, hoc: any) => hoc(it), GroupButtonInternal);

export default GroupButton;
